import styled from 'styled-components';

import { Badge, BadgeVariant } from '@components/badge';
import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { Card } from '@components/card';
import { useDeleteModal } from '@components/generic-modals';
import { IconBrandJourneys } from '@components/icons/brand-journeys';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconIntegrations } from '@components/icons/integrations';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconJourneyLogoPlain } from '@components/icons/journey-logo-plain';
import { IconMessages } from '@components/icons/messages';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IconSend } from '@components/icons/send';
import { IconTrash } from '@components/icons/trash';
import { ListCard } from '@components/list-card';
import { RelativeSpinner, Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { StatusPiechartCircle } from '@components/piechart-circle/piechart-circle';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useIntercom } from '@hooks/use-intercom';
import { useScopedTranslate } from '@locale/translate';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';

import { config } from 'app-config';
import { usePermissions } from 'features/permissions';
import { useAddToJourneySidebar } from 'integrations/journeys/add-to-journey-sidebar';
import { ApplicantJourneyType, JourneyStatus } from 'integrations/journeys/api';
import { JourneyListError } from 'integrations/journeys/components/list-states';
import {
  useApplicationJourneys,
  useRemoveFromJourneys,
  useToken,
} from 'integrations/journeys/hooks';
import { IApplication } from 'models/types';

export const ApplicationJourneys = ({ application }: { application: IApplication }) => {
  const tokenQuery = useToken();

  if (tokenQuery.isLoading || !tokenQuery.data) {
    return (
      <Content>
        <Spinner />
      </Content>
    );
  }
  const isConnected = tokenQuery.data.ok;
  const isTokenValid = tokenQuery.data.valid;

  return (
    <Content>
      {!isConnected ? (
        <JourneysNotConnected />
      ) : !isTokenValid ? (
        <JourneysTokenInvalid />
      ) : (
        <JourneysConnected application={application} />
      )}
    </Content>
  );
};

const JourneysConnected = ({ application }: { application: IApplication }) => {
  const { t } = useScopedTranslate(locale);
  const journeyQuery = useApplicationJourneys(application.id);
  const journeys = journeyQuery.data ?? [];
  const isLoading = journeyQuery.isLoading;
  if (isLoading) {
    return (
      <ConnectedContent>
        <RelativeSpinner />
      </ConnectedContent>
    );
  }

  // A failed fetch is not an empty list. Falling through to NoJourneys told the
  // recruiter "This applicant is not in a journey" whenever the Journeys API
  // hiccuped, hiding real journeys and inviting a duplicate send (F11).
  if (journeyQuery.isError) {
    return (
      <ConnectedContent>
        <JourneyListError
          dataCy="applicant-journeys-error"
          description={t('journeysLoadFailed')}
          retryLabel={t('retry')}
          onRetry={() => journeyQuery.refetch()}
        />
      </ConnectedContent>
    );
  }

  return (
    <ConnectedContent>
      {journeys.length > 0 ? (
        <JourneysList journeys={journeys} application={application} />
      ) : (
        <NoJourneys application={application} />
      )}
    </ConnectedContent>
  );
};
const JourneysList = ({
  journeys,
  application,
}: {
  journeys: ApplicantJourneyType[];
  application: IApplication;
}) => {
  const { t } = useScopedTranslate(locale);
  const showSidebar = useAddToJourneySidebar();
  const { hasRolePermission } = usePermissions();
  return (
    <Stack space={4}>
      <Stack space={2}>
        {journeys.map((journey) => (
          <JourneyCard key={journey.id} journey={journey} applicationId={application.id} />
        ))}
      </Stack>
      {hasRolePermission('application:add-journeys') && (
        <div>
          <Button
            variant="secondary"
            iconLeft={<IconSend />}
            text={t('addToJourney')}
            onClick={() =>
              showSidebar({ applications: [application.id], jobId: application.job.id })
            }
          />
        </div>
      )}
    </Stack>
  );
};

type StatusMapType = Record<JourneyStatus, { text: string; tagVariant: BadgeVariant }>;

const JourneyCard = ({
  journey,
  applicationId,
}: {
  journey: ApplicantJourneyType;
  applicationId: number;
}) => {
  const { t } = useScopedTranslate(locale);
  const showRemoveModal = useDeleteModal();
  const removeMutation = useRemoveFromJourneys(applicationId, journey.id);

  const statusMap: StatusMapType = {
    not_started: {
      text: t('notStarted'),
      tagVariant: 'indigo',
    },
    paused: {
      text: t('paused'),
      tagVariant: 'neutral',
    },
    in_progress: {
      text: t('inProgress'),
      tagVariant: 'orange',
    },
    completed: {
      text: t('completed'),
      tagVariant: 'green',
    },
  };

  const status = statusMap[journey.status];

  const numNotStarted =
    journey.numActions -
    journey.numCompleted -
    journey.numInProgress -
    journey.numHalted -
    journey.numToBeStarted;

  const progress: { color: Color; value: number }[] = [
    {
      color: 'green100',
      value: journey.numCompleted,
    },
    {
      color: 'orange100',
      value: journey.numInProgress,
    },
    {
      color: 'indigo100',
      value: journey.numToBeStarted,
    },
    {
      color: 'purple50',
      value: journey.numHalted,
    },
    {
      color: 'purple20',
      value: numNotStarted,
    },
  ];

  const journeysUrl = `${config.JOURNEYS_URL}/workspaces/${journey.workspaceId}/journeys/${journey.journeyId}/travellers/${journey.id}/travellerjourney`;

  return (
    <ListCard
      title={journey.journeyTitle}
      description={journey.journeyDescription}
      spaceX={4}
      spaceY={4}
      details={
        <Text
          font="regular14"
          color="purple80"
          text={t('added', {
            date_added: abbreviateDate(journey.dateCreated, 'en', 'datetime'),
          })}
        />
      }
      icon={<IconJourneyItem />}
      actions={
        <Stack space={4} orientation="horizontal" align="center">
          <Badge variant={status.tagVariant} text={status.text} />
          <StatusPiechartCircle size={3} sections={progress} status={journey.status} />
          <Menu buttonProps={{ variant: 'ghost', iconRight: <IconMoreVertical /> }}>
            <Menu.Item
              label={
                <Stack space={1} orientation="horizontal" align="center">
                  {t('openJourney')} <IconOpenNewTab size={16} />
                </Stack>
              }
              icon={<IconJourneyItem />}
              onSelect={() => window.open(journeysUrl, '_blank')}
            />
            <Menu.Item
              label={t('removeFromJourney')}
              variant="danger"
              icon={<IconTrash />}
              onSelect={() =>
                showRemoveModal({
                  title: t('removeFromJourney'),
                  description: t('removeFromJourneyDescription'),
                  hasUserConfirmation: true,
                  onConfirm: () => {
                    removeMutation.mutateAsync();
                  },
                })
              }
            />
          </Menu>
        </Stack>
      }
    />
  );
};

const NoJourneys = ({ application }: { application: IApplication }) => {
  const { t } = useScopedTranslate(locale);
  const showSidebar = useAddToJourneySidebar();
  const { hasRolePermission } = usePermissions();
  return (
    <Card spaceX={6} spaceY={4} variant="gray">
      <Stack orientation="horizontal" justify="space-between" align="center">
        <Stack orientation="horizontal" space={4}>
          <IconJourneyItem />
          <Text font="semibold14" color="purple80" text={t('applicantNotInJourney')} />
        </Stack>
        {hasRolePermission('application:add-journeys') && (
          <Button
            variant="secondary"
            iconLeft={<IconSend />}
            text={t('addToJourney')}
            onClick={() =>
              showSidebar({ applications: [application.id], jobId: application.job.id })
            }
          />
        )}
      </Stack>
    </Card>
  );
};

const JourneysNotConnected = () => {
  const { t } = useScopedTranslate(locale);
  const { showMessenger, isEnabled: isIntercomEnabled } = useIntercom();
  const { hasRolePermission } = usePermissions();
  const canViewIntegrationsSettings = hasRolePermission('company:view-integrations-settings');
  // Shadow uses IconJourneyLogoPlain (currentColor) so it can be tinted faint;
  // IconBrandJourneys hard-codes its fill and can't be recolored for the shadow.
  const shadow = <IconJourneyLogoPlain size={120} color="purpleTrans5" />;

  const bulletPoints = [
    t('addApplicantsToJourneys'),
    t('viewJourneyProcess'),
    t('removeApplicants'),
  ];
  return (
    <InvalidCredentialsCard>
      <Stack space={6}>
        <Stack space={6} orientation="horizontal" align="center">
          <div>
            <IconCard>
              <IconBrandJourneys size={40} />
              <Shadow>{shadow}</Shadow>
            </IconCard>
          </div>
          <Stack space={1}>
            <Text font="bold18" text={t('journeys')} />
            <Text font="regular14" color="purple80" text={t('addJourneysDescription')} />
          </Stack>
        </Stack>
        <Stack space={2}>
          <Text font="bold14" text={t('withThisIntegration')} />
          <div>
            {bulletPoints.map((text) => (
              <Stack space={1} orientation="horizontal" align="center" key={text}>
                <IconCheckmark color="blue100" />
                <Text font="regular14" text={text} />
              </Stack>
            ))}
          </div>
        </Stack>
        <Stack space={6} orientation="horizontal" style={{ height: pxToRem(68) }}>
          <Stack space={2}>
            <Text font="regular14" text={t('dontHaveJourneys')} />
            <ButtonGroup space={4} align="left">
              <Button
                iconLeft={<IconMessages />}
                text={t('chatWithUs')}
                onClick={showMessenger}
                disabled={!isIntercomEnabled}
              />
              <Button
                variant="secondary"
                iconRight={<IconOpenNewTab />}
                text={t('exploreJourneys')}
                href="https://www.50skills.com/product-tour"
                target="_blank"
              />
            </ButtonGroup>
          </Stack>
          {canViewIntegrationsSettings && (
            <>
              <Separator orientation="horizontal" />
              <Stack space={2}>
                <Text font="regular14" text={t('alreadyHaveJourneys')} />
                <div>
                  <Button
                    variant="secondary"
                    iconLeft={<IconIntegrations />}
                    text={t('connect')}
                    href="/settings/apps/journeys"
                  />
                </div>
              </Stack>
            </>
          )}
        </Stack>
      </Stack>
    </InvalidCredentialsCard>
  );
};

const JourneysTokenInvalid = () => {
  const { t } = useScopedTranslate(locale);
  const { hasRolePermission } = usePermissions();
  const canViewIntegrationsSettings = hasRolePermission('company:view-integrations-settings');

  return (
    <InvalidCredentialsCard>
      <Stack space={4} align="center">
        <IconIntegrations size={48} color="orange100" />
        <Stack space={2} align="center">
          <Text font="bold16" text={t('tokenInvalid')} />
          <Text font="regular14" color="purple80" text={t('tokenInvalidDescription')} />
        </Stack>
        {canViewIntegrationsSettings && (
          <Button
            iconLeft={<IconIntegrations />}
            text={t('reconnect')}
            href="/settings/apps/journeys"
          />
        )}
      </Stack>
    </InvalidCredentialsCard>
  );
};

const Content = styled.div({ marginTop: pxToRem(24) });
const ConnectedContent = styled.div({ maxWidth: pxToRem(860) });

const InvalidCredentialsCard = styled(Card).attrs({ variant: 'gray', space: 6 })({
  maxWidth: pxToRem(860),
});

const IconCard = styled.div({
  backgroundColor: theme.color.white100,
  boxShadow: theme.shadow.fancy,
  overflow: 'hidden',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '0.5rem',
  font: theme.font.yuge,
  width: '5rem',
  height: '5rem',
});
const Shadow = styled.div({
  position: 'absolute',
  right: 0,
  bottom: 0,
  transform: 'translateY(16px)',
});

const locale = {
  en: {
    journeys: 'Journeys',
    addJourneysDescription:
      'Empower your hiring processes by connecting to Journeys. Create and manage automated workflows and seamlessly add applicants from here to the journey of your choice. Introduce new possibilities to your hiring process such as pre-boarding, cross-boarding, evaluation, interviews & really anything you can dream of!',
    withThisIntegration: 'With this integration, you can',
    addApplicantsToJourneys: 'Add your applicants and hires to journeys',
    viewJourneyProcess: 'View the journey progress status of each applicant',
    removeApplicants: 'Remove applicants from active journeys',
    dontHaveJourneys: "Don't have Journeys?",
    alreadyHaveJourneys: 'Already have Journeys?',
    chatWithUs: 'Chat with us',
    exploreJourneys: 'Explore Journeys',
    connect: 'Connect',
    applicantNotInJourney: 'This applicant is not in a journey',
    journeysLoadFailed: "Could not load this applicant's journeys. Please try again.",
    retry: 'Try again',
    addToJourney: 'Add to Journey',
    notStarted: 'Not started',
    paused: 'Paused',
    inProgress: 'In progress',
    completed: 'Completed',
    removeFromJourney: 'Remove from journey',
    openJourney: 'Open journey',
    removeFromJourneyDescription:
      'Are you sure you want to remove the following applicant from this journey? All journey details and progress will be lost',
    added: 'Added: {date_added}',
    tokenInvalid: 'Connection Issue',
    tokenInvalidDescription:
      'Your credentials have either expired or been removed, please reconnect to re-enable this integration',
    reconnect: 'Reconnect',
  },
  is: {
    journeys: 'Journeys',
    addJourneysDescription:
      'Uppfærðu ráðningarferlin með því að tengjast Journeys. Búðu til sjálfvirk ferli og sendu þína umsækjendur í þau, með einum smelli. Bættu við nýjum möguleikum í ráðningarferil þíns fyrirtækis til dæmis ráðningar innanhús, endurráðningar, viðtalsferlar, endurmat og í raun allir ferlar sem þig getur dreymt um!',
    withThisIntegration: 'Með þessari tengingu færðu möguleikann',
    addApplicantsToJourneys: 'Sendu þína umsækjendur  í journeys',
    viewJourneyProcess: 'Yfirsýn yfir stöðu umsækjanda í viðeigandi journey',
    removeApplicants: 'Fjarlægja umsækjendur úr journey',
    dontHaveJourneys: 'Ekki með Journeys?',
    alreadyHaveJourneys: 'Nú þegar með Journeys?',
    chatWithUs: 'Hafðu samband',
    exploreJourneys: 'Hvað er Journeys?',
    connect: 'Tengja',
    applicantNotInJourney: 'Þessi umsækjandi er ekki í virku journey',
    journeysLoadFailed: 'Ekki tókst að sækja Journey-ferli þessa umsækjanda. Reyndu aftur.',
    retry: 'Reyna aftur',
    addToJourney: 'Senda í journey',
    notStarted: 'Not started',
    paused: 'Paused',
    inProgress: 'In progress',
    completed: 'Completed',
    removeFromJourney: 'Fjarlægja úr journey',
    openJourney: 'Opna journey',
    removeFromJourneyDescription:
      'Ertu viss um að þú viljir fjarlægja þessa umsókn úr journey, og allar upplýsingar með?',
    added: 'Bætt við: {date_added}',
    tokenInvalid: 'Tengingarvandamál',
    tokenInvalidDescription:
      'Þínar aðgangssupplýsingar eru útrunnar eða hafa verið fjarlægðar, vinsamlegast setjið upp tenginguna aftur',
    reconnect: 'Endurtengjast',
  },
};
